import { Directive, ElementRef, OnDestroy, Optional, Self } from '@angular/core'
import { Tooltip } from 'primeng/tooltip'

/** Gap kept between a tooltip and the viewport edge. */
const VIEWPORT_MARGIN = 8

/** Keeps the whole arrow triangle (2 × the 0.25rem gutter) on the bubble. */
const ARROW_INSET = 8

export interface TooltipBox {
  left: number
  top: number
  width: number
  height: number
}

export interface TooltipFitResult {
  /** Viewport-relative position for the container. */
  left: number
  top: number
  /** Container-relative offset for the arrow, so it still points at the target. */
  arrowLeft: number
  placeBelow: boolean
}

export function overflowsViewport(left: number, width: number, viewportWidth: number): boolean {
  return left < VIEWPORT_MARGIN || left + width > viewportWidth - VIEWPORT_MARGIN
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

/**
 * Re-places a tooltip that PrimeNG left hanging off screen. Above and below are the only
 * sides worth considering: a bubble placed left or right is bounded by the gap between the
 * target and the viewport edge, which is what ran out of room in the first place.
 */
export function computeTooltipFit(
  container: TooltipBox,
  host: TooltipBox,
  viewport: { width: number; height: number },
  currentlyBelow: boolean
): TooltipFitResult {
  const roomAbove = host.top - container.height >= VIEWPORT_MARGIN
  const placeBelow = currentlyBelow || !roomAbove

  const left = clamp(
    host.left + host.width / 2 - container.width / 2,
    VIEWPORT_MARGIN,
    viewport.width - VIEWPORT_MARGIN - container.width
  )

  const top = clamp(
    placeBelow ? host.top + host.height : host.top - container.height,
    VIEWPORT_MARGIN,
    viewport.height - VIEWPORT_MARGIN - container.height
  )

  return {
    left,
    top,
    arrowLeft: clamp(host.left + host.width / 2 - left, ARROW_INSET, container.width - ARROW_INSET),
    placeBelow,
  }
}

/**
 * PrimeNG places a tooltip beside its target and, if it doesn't fit, walks
 * top → bottom → right → left looking for a side with room — but it never clamps, so a
 * bubble too wide for every side is simply left off screen. This pulls that case back in and
 * moves the arrow to keep it pointing at the target.
 */
@Directive({
  selector: '[pTooltip]',
  standalone: false,
})
export class TooltipViewportFitDirective implements OnDestroy {
  private readonly originalAlign?: () => void

  constructor(
    @Optional() @Self() private tooltip: Tooltip,
    private el: ElementRef<HTMLElement>
  ) {
    if (!this.tooltip) {
      return
    }
    // Wrapping align() rather than a hover listener catches the whole fallback chain, plus
    // the re-aligns PrimeNG runs when the content or position changes while a tooltip is open.
    this.originalAlign = this.tooltip.align.bind(this.tooltip)
    this.tooltip.align = () => {
      this.originalAlign?.()
      this.fitIntoViewport()
    }
  }

  ngOnDestroy(): void {
    if (this.tooltip && this.originalAlign) {
      this.tooltip.align = this.originalAlign
    }
  }

  private fitIntoViewport(): void {
    const container: HTMLElement | undefined = this.tooltip?.container
    if (!container) {
      return
    }

    const rect = container.getBoundingClientRect()
    const viewport = { width: window.innerWidth, height: window.innerHeight }
    if (!overflowsViewport(rect.left, rect.width, viewport.width)) {
      return
    }

    const placeBelow = container.classList.contains('p-tooltip-bottom')
    container.classList.remove('p-tooltip-left', 'p-tooltip-right', 'p-tooltip-top', 'p-tooltip-bottom')
    container.classList.add(placeBelow ? 'p-tooltip-bottom' : 'p-tooltip-top')

    // The class swap moves the root's gutter padding to the vertical axis, so the box has to
    // be measured again before it can be centered on the target.
    const swapped = container.getBoundingClientRect()
    const host = this.el.nativeElement.getBoundingClientRect()
    const fit = computeTooltipFit(
      { left: swapped.left, top: swapped.top, width: swapped.width, height: swapped.height },
      { left: host.left, top: host.top, width: host.width, height: host.height },
      viewport,
      placeBelow
    )

    if (fit.placeBelow !== placeBelow) {
      container.classList.remove('p-tooltip-top')
      container.classList.add('p-tooltip-bottom')
    }
    container.style.left = `${fit.left + window.scrollX}px`
    container.style.top = `${fit.top + window.scrollY}px`

    const arrow = container.querySelector<HTMLElement>('[data-pc-section="arrow"]')
    if (arrow) {
      arrow.style.right = ''
      arrow.style.top = fit.placeBelow ? '0' : ''
      arrow.style.bottom = fit.placeBelow ? '' : '0'
      arrow.style.left = `${fit.arrowLeft}px`
    }
  }
}
